import { CalendarClock, FolderOpen } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useUiLocale } from '@/contexts/UiLocaleContext'

import { POS_GENDER_TOGGLE_OPTIONS } from './posCatalog'

const GENDER_TITLE_KEYS = {
  ladies: 'pos.ladies',
  gents: 'pos.gents',
  all: 'pos.allCategories',
}

/**
 * @param {object} props
 * @param {import('./posCatalog').PosCatalogGenderFilter} props.catalogGenderFilter
 * @param {(filter: import('./posCatalog').PosCatalogGenderFilter) => void | Promise<void>} props.onGenderChange
 * @param {typeof import('./posMockData').POS_SALE_META} props.sale
 * @param {number} [props.heldTicketCount]
 * @param {() => void | Promise<void>} [props.onLoadTicket]
 * @param {() => void} [props.onLoadAppointment]
 * @param {boolean} [props.disabled]
 */
export function PosHeader({
  catalogGenderFilter,
  onGenderChange,
  sale,
  heldTicketCount = 0,
  onLoadTicket,
  onLoadAppointment,
  disabled = false,
}) {
  const { t } = useUiLocale()

  return (
    <header className="pos-v2-header flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-md border border-[#D1D5DB] bg-white px-2 py-1">
      <div className="flex min-w-0 items-center gap-2 text-xs">
        <span className="truncate font-bold text-slate-900">{sale.sale_number}</span>
        <span className="text-slate-300" aria-hidden>
          ·
        </span>
        <span className="truncate text-slate-600">{sale.branch_label}</span>
        <span className="hidden text-slate-400 md:inline">· {sale.opened_at}</span>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {onLoadTicket ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onLoadTicket}
            className={cn(
              'pos-v2-load-ticket-btn inline-flex items-center gap-1.5 rounded-md border-2 px-2.5 py-1.5 text-xs font-bold transition-all touch-manipulation',
              heldTicketCount > 0
                ? 'border-amber-400 bg-amber-50 text-amber-950 shadow-sm ring-2 ring-amber-200/80 hover:bg-amber-100'
                : 'border-[#D1D5DB] bg-[#F8FAFC] text-slate-700 hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
              disabled && 'opacity-50',
            )}
          >
            <FolderOpen className="h-4 w-4 shrink-0" aria-hidden />
            <span>{t('pos.loadTicket')}</span>
            <kbd className="rounded bg-white/90 px-1 py-0.5 font-mono text-[10px] font-bold text-[#7C3AED] shadow-sm">
              F10
            </kbd>
            {heldTicketCount > 0 ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                {heldTicketCount > 9 ? '9+' : heldTicketCount}
              </span>
            ) : null}
          </button>
        ) : null}

        {onLoadAppointment ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onLoadAppointment}
            className="inline-flex items-center gap-1 rounded-md border border-[#D1D5DB] bg-[#F8FAFC] px-2 py-1.5 text-[11px] font-semibold text-slate-700 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
          >
            <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="hidden sm:inline">{t('pos.loadAppointment')}</span>
            <kbd className="rounded bg-white px-1 py-0.5 font-mono text-[9px] font-bold text-slate-500">F3</kbd>
          </button>
        ) : null}

        <div
          className="flex shrink-0 rounded-md border border-[#D1D5DB] bg-[#F8FAFC] p-0.5"
          role="group"
          aria-label={t('pos.genderPricing')}
        >
          {POS_GENDER_TOGGLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              title={t(GENDER_TITLE_KEYS[option.value])}
              onClick={() => onGenderChange(option.value)}
              className={cn(
                'h-8 min-w-8 rounded px-2.5 text-xs font-semibold transition-colors touch-manipulation',
                catalogGenderFilter === option.value
                  ? 'bg-[#7C3AED] text-white'
                  : 'text-slate-600 hover:text-slate-900',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </header>
  )
}
